import { v4 as uuid } from "uuid";
import { Divider } from "@mui/material";
import Card from "../../../components/card/card";
import { useFormContext } from "react-hook-form";
import { useContext, useEffect, useMemo, useState } from "react";
import { AppointmentStyled } from "./appointments.styles";
import { getAge, isSomething } from "../../../../../utils/common-utils";
import { useAppDispatch } from "../../../../../store/hooks";
import Skeleton from "../../../../../components/skeleton/skeleton";
import {
  submitUserTimeSlotsRequest,
  getAllUserAppointmentsTypes,
} from "../../../../../actions";
import { addNotification } from "../../../../../store/slices/ui-slice";
import { getParsedAppointments, getPayload } from "./appointments.utils";
import {
  USER_APPOINTMENT_ACTIONS,
  UserAppointmentContext,
} from "../../../context/user-appointment-context";
import SelectFormField from "../../../../../components/form-fields/select-field/select-form-field";
import TextInputFormField from "../../../../../components/form-fields/text-input-field/text-input-form-field";

const Appointments = () => {
  const {
    state: {
      selectedClientAccount,
      activeStep,
      selectedPets,
      appointmentTypes,
      provider
    },
    userDispatcher,
  } = useContext(UserAppointmentContext);
  const { control, handleSubmit, watch } = useFormContext();
  const dispatch = useAppDispatch();
  const values = watch();

  const [loading, setLoading] = useState(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState(false);

  useEffect(() => {
    const fetchPets = async () => {
      setLoading(true);
      try {
        const response = await getAllUserAppointmentsTypes();
        const parsedData = getParsedAppointments(response?.data?.data) || [];
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_APPOINTMENT_TYPES,
          payload: parsedData,
        });
      } catch (err: any) {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: err.message || "An unexpected error occurred.",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPets();
  }, [dispatch, userDispatcher]);

  const onHandleAppointments = async (formValues: any) => {
    try {
      setLoadingConfirmBtn(true);
      const payload = getPayload(
        formValues,
        selectedPets,
        selectedClientAccount,
        provider
      );
      const response = await submitUserTimeSlotsRequest(payload);
      userDispatcher({
        type: USER_APPOINTMENT_ACTIONS.SET_TIME_SLOTS,
        payload: response?.data?.data,
      });
      userDispatcher({
        type: USER_APPOINTMENT_ACTIONS.SET_ACTIVE_STEP,
        payload: activeStep + 1,
      });
    } catch (err: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: err.message || "An unexpected error occurred.",
        })
      );
    } finally {
      setLoadingConfirmBtn(false);
    }
  };

  const isEnable = useMemo(
    () =>
      selectedPets?.every(
        (pet: any) =>
          isSomething(values?.[`appointment_type_${pet?.id}`]) &&
          isSomething(values?.[`reason_${pet?.id}`])
      ),
    [selectedPets, values]
  );
  return (
    <Card
      title={"Select appointment types"}
      description={"Choose appointment type for each pet list below."}
      btnTitle={"Continue"}
      onHandleClick={handleSubmit(onHandleAppointments)}
      progress={activeStep * 5}
      isLoadingConfirmBtn={loadingConfirmBtn}
      disabledConfirmBtn={!isEnable}
      showBackButton
      handleBack={() =>
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_ACTIVE_STEP,
          payload: activeStep - 1,
        })
      }
    >
      <AppointmentStyled>
        {loading && (
          <>
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} height={150} />
            ))}
          </>
        )}
        {!loading &&
          selectedPets &&
          selectedPets.map((pet: any) => {
            // Find the selected appointment type label for this pet
            const selectedTypeId = values?.[`appointment_type_${pet?.id}`];
            const selectedType = appointmentTypes?.find(
              (type: any) => type.value === selectedTypeId
            );
            const description =
              selectedType && selectedType.description;

            return (
              <div className="card" key={pet?.id}>
                <div className="row">
                  <div>
                    <div className="label">Name</div>
                    <div className="value">{pet.name}</div>
                  </div>
                  <div>
                    <div className="label">Species</div>
                    <div className="value">{pet.species[0]?.label}</div>
                  </div>
                  <div>
                    <div className="label">Gender</div>
                    <div className="value">{pet?.genders?.[0]?.label}</div>
                  </div>
                  <div>
                    <div className="label">Breed</div>
                    <div className="value">{pet?.breeds?.[0]?.label}</div>
                  </div>
                  <div>
                    <div className="label">Age</div>
                    <div className="value">{getAge(pet.birthdate) || "--"}</div>
                  </div>
                </div>
                <Divider />
                <SelectFormField
                  name={`appointment_type_${pet?.id}`}
                  control={control}
                  placeholder="Select here"
                  label="Appointment type"
                  options={appointmentTypes}
                />
                <TextInputFormField
                  name={`reason_${pet?.id}`}
                  control={control}
                  label="Reason"
                  placeholder="Enter here"
                />
                {/* Show description if appointment type is selected */}
                {description && (
                  <div style={{ marginTop: 8, color: "#2460b8", fontSize: 14 }}>
                    <strong>Description:</strong> {description}
                  </div>
                )}
              </div>
            );
          })}
      </AppointmentStyled>
    </Card>
  );
};

export default Appointments;
